import { useCallback, useEffect, useState } from 'react';
import useShopify from './useShopify';
import { apiFetch } from '../lib/api';


/**
 * useBilling — single source of truth for the merchant's plan state.
 *
 * Returns:
 *   {
 *     plan:           'standard' | 'growth' | 'pro' | 'unlimited' | null,
 *     planName:       string | null,           // human label e.g. "Pro"
 *     expired:        boolean,                 // true → trial finished, no sub
 *     trialActive:    boolean,
 *     trialDaysLeft:  number,
 *     features:       object | null,           // null when expired
 *     subscription:   object | null,
 *     loading:        boolean,
 *     error:          string | null,
 *     refresh:        () => Promise<void>,
 *     subscribe:      (planKey) => Promise<{ confirmationUrl }>,
 *   }
 *
 * `features` keys:
 *   productsPerMonth, imageAdsPerMonth, websiteBuilder,
 *   adsManager, aiChat, unlimited
 */
export default function useBilling() {
  const { shop, authenticated } = useShopify();
  const [state, setState] = useState({
    plan: null,
    planName: null,
    expired: false,
    trialActive: false,
    trialDaysLeft: 0,
    features: null,
    subscription: null,
    loading: true,
    error: null,
  });

  const fetchStatus = useCallback(async () => {
    if (!shop) {
      setState((prev) => ({ ...prev, loading: false }));
      return;
    }
    setState((prev) => ({ ...prev, loading: true, error: null }));
    try {
      const res = await apiFetch(
        `/api/billing/status`,
        { credentials: 'include' },
      );
      // 401 → unauthenticated; treat as still-loading rather than expired.
      if (res.status === 401) {
        setState({
          plan: null, planName: null, expired: false,
          trialActive: false, trialDaysLeft: 0,
          features: null, subscription: null,
          loading: false, error: null,
        });
        return;
      }
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || `Status ${res.status}`);

      setState({
        plan: data.plan || null,
        planName: planLabel(data.plan, data.trialActive),
        expired: !!data.expired,
        trialActive: !!data.trialActive,
        trialDaysLeft: Number(data.trialDaysLeft) || 0,
        // One trial per store, ever — when true the plans UI stops
        // advertising the 3-day trial (subscriptions start paid).
        trialUsed: !!data.trialUsed,
        features: data.features || null,
        subscription: data.subscription || null,
        loading: false,
        error: null,
      });
    } catch (err) {
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err.message || 'Unable to load billing status',
      }));
    }
  }, [shop]);

  useEffect(() => {
    if (authenticated && shop) fetchStatus();
    else setState((prev) => ({ ...prev, loading: false }));
  }, [authenticated, shop, fetchStatus]);

  const subscribe = useCallback(async (planKey) => {
    if (!shop) throw new Error('No shop in context');
    const res = await apiFetch(`/api/billing/subscribe`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ shop, planKey }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.error || `Subscribe failed (${res.status})`);
    return data;
  }, [shop]);

  return { ...state, refresh: fetchStatus, subscribe };
}

function planLabel(plan, trialActive) {
  if (trialActive && !plan) return 'Free Trial';
  if (!plan) return null;
  return plan.charAt(0).toUpperCase() + plan.slice(1);
}

export { useBilling };
